import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as signalR from '@microsoft/signalr';
import { fetchJobById } from '../api/jobsApi';

const HUB_URL = `${import.meta.env.VITE_API_URL}/hub/liverefresh`;

// Mirror of useCustomer. The ViewJob page currently shows read-only job
// metadata, but the dirty-guard plumbing is already here for when the job
// details card (with debounced autosave) lands later — at that point we
// won't need to touch this hook.
export default function useJob(id, isDirtyRef) {
    const queryClient = useQueryClient();
    const pendingRefreshRef = useRef(false);

    const query = useQuery({
        queryKey: ['job', id],
        queryFn: () => fetchJobById(id),
        enabled: id != null,
        retry: false,
    });

    useEffect(() => {
        if (id == null) return undefined;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, {
                accessTokenFactory: () =>
                    window.Clerk.session.getToken({ template: 'marginpal-api' }),
            })
            .withAutomaticReconnect()
            .build();

        connection.on('UpdateJobTable', () => {
            if (isDirtyRef?.current) {
                pendingRefreshRef.current = true;
            } else {
                queryClient.invalidateQueries({ queryKey: ['job', id] });
            }
            // Propagate to the list cache so JobsTable is fresh when the
            // user navigates back (mirrors useQuote → ['quotes'] pattern).
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
        });

        connection.start().catch(err => console.error('SignalR connection error:', err));

        return () => {
            connection.stop();
        };
    }, [queryClient, id, isDirtyRef]);

    const flushPendingRefresh = () => {
        if (pendingRefreshRef.current) {
            pendingRefreshRef.current = false;
            queryClient.invalidateQueries({ queryKey: ['job', id] });
        }
    };

    return { ...query, flushPendingRefresh };
}
